import { extractMath } from 'extract-math';
import * as katex from 'katex';
import { uniqBy } from 'lodash-es';
import { DeviceDetectorService } from 'ngx-device-detector';
import * as CryptoJS from 'crypto-js';
import { Permissions } from 'src/app/models/session';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { ParsedMailbox } from 'email-addresses';
import * as emailAddresses from 'email-addresses';
import { environment } from '../../../environments/environment';
import { isValidFirebaseEmail, parseAndValidateEmail } from './form-validators';

const hasKey = (obj: object, property: string) =>
  Object.prototype.hasOwnProperty.call(obj, property);

export function sortByNum(first: object, second: object, property: string) {
  if (property && hasKey(first, property) && hasKey(second, property) && property) {
    if (first[property] > second[property]) {
      return 1;
    } else if (first[property] < second[property]) {
      return -1;
    }
  }
  return 0;
}

export function sortByDate(first: object, second: object, property: string, order = 1) {
  if (property && hasKey(first, property) && hasKey(second, property) && property) {
    if (new Date(first[property]).getTime() > new Date(second[property]).getTime()) {
      return order * 1;
    } else if (new Date(first[property]).getTime() < new Date(second[property]).getTime()) {
      return order * -1;
    }
  }
  return 0;
}

export function sortByLength(first: Array<any>, second: Array<any>) {
  if (first.length > second.length) {
    return -1;
  } else if (first.length < second.length) {
    return 1;
  }
  return 0;
}

export function sortByString(first: object, second: object, property: string) {
  if (property && hasKey(first, property) && hasKey(second, property) && property) {
    if (first[property].toLowerCase() > second[property].toLowerCase()) {
      return 1;
    } else if (first[property].toLowerCase() < second[property].toLowerCase()) {
      return -1;
    }
  }
  return 0;
}

export function capitalize(txt: string): string {
  if (!txt) {
    return '';
  }
  return txt.charAt(0).toUpperCase() + txt.slice(1);
}

/**
 * Extracts the timestamp from a MongoDB ObjectId string
 * @param objectId The MongoDB ObjectId as a string (24 hex characters)
 * @returns Date object representing when the document was created
 */
export function getObjectIdTimestamp(objectId: string): Date {
  if (!objectId || objectId.length !== 24) {
    throw new Error('Invalid ObjectId: must be a 24-character hex string');
  }
  // Extract first 8 characters (4 bytes) which represent Unix timestamp in seconds
  const timestamp = parseInt(objectId.substring(0, 8), 16);
  return new Date(timestamp * 1000); // Convert seconds to milliseconds
}

export function addFullScreenListener(callback) {
  document.addEventListener(
    'fullscreenchange',
    () => {
      callback();
    },
    false,
  );

  document.addEventListener(
    'mozfullscreenchange',
    () => {
      callback();
    },
    false,
  );

  document.addEventListener(
    'webkitfullscreenchange',
    () => {
      callback();
    },
    false,
  );

  document.addEventListener(
    'msfullscreenchange',
    () => {
      callback();
    },
    false,
  );
}

export function exitFullScreen() {
  const methodToBeInvoked =
    document['exitFullscreen'] ||
    document['mozCancelFullscreen'] ||
    document['webkitExitFullscreen'] ||
    document['msExitFullscreen'];
  if (
    methodToBeInvoked &&
    (document['fullscreen'] ||
      document['msFullscreenElement'] ||
      document['mozFullScreen'] ||
      document['webkitIsFullScreen'])
  ) {
    methodToBeInvoked.call(document);
  }
}

export function goFullScreen(element) {
  const methodToBeInvoked =
    element['requestFullscreen'] ||
    element['mozRequestFullscreen'] ||
    element['webkitRequestFullscreen'] ||
    element['msRequestFullscreen'];
  if (methodToBeInvoked) {
    methodToBeInvoked.call(element);
  }
}

export function checkFullScreen() {
  return (
    document['fullscreen'] ||
    document['msFullscreenElement'] ||
    document['mozFullScreen'] ||
    document['webkitIsFullScreen']
  );
}

export function b64toBlob(b64Data, contentType = '', sliceSize = 512): Blob | null {
  const byteCharacters = atob(b64Data);
  const byteArrays: Uint8Array[] = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export function getHtmlFromFormattedText(text: string, fromTable = false): string {
  let str = text
    .replace(/\*\*\*(.*?)\*\*\*/g, function (outer, inner) {
      return `<strong><em>${inner}</em></strong>`;
    })
    .replace(/\*\*(.*?)\*\*/g, function (outer, inner) {
      return `<strong>${inner}</strong>`;
    })
    .replace(/\*(.*?)\*/g, function (outer, inner) {
      return `<em>${inner}</em>`;
    })
    .replace('<pre><code>', '<p>')
    .replace('</code></pre>', '</p>');
  if (fromTable) {
    str = str.replace(/\n/g, '<br>');
  }
  return str;
}

const userMentionReplacementString = `
  <span class="mention" data-denotation-char="@" data-id="$1" data-value="$2">
    <span contenteditable="false"><span class="ql-mention-denotation-char">@</span>$2</span>
  </span>
`;

export const renderUserMentions = (markdown: string): string =>
  markdown.replace(
    /<user-mention data-id="(.+?)" data-name="(.+?)" \/>/g,
    userMentionReplacementString,
  );

export function getConvertedLatex(htmlStr: string): string {
  let convertedLatex = htmlStr
    .replace(/&amp;/g, '& ')
    .replace(/&lt;/g, 'lt ')
    .replace(/&gt;/g, 'gt ')
    .replace(/&quot;/g, "''");
  if (!convertedLatex.includes('!\\')) {
    convertedLatex = convertedLatex.replace(/\\!/g, '!');
  }
  return convertedLatex;
}

export function getConvertedHtml(htmlStr: string): string {
  let convertedHtml = '';
  extractMath(htmlStr, {
    delimiters: {
      display: ['$$', '$$'],
    },
  }).forEach((html) => {
    if (html.math) {
      const convertedLatex = getConvertedLatex(html.raw);
      convertedHtml += `<span class="ql-formula" data-value="${convertedLatex}">
        <span contenteditable="false">${katex.renderToString(convertedLatex)}</span></span>`;
    } else {
      convertedHtml += html.raw.replace(/\\\$/g, '$').replace(/\\\)/g, ')').replace(/\\\./g, '.');
    }
  });
  convertedHtml = renderUserMentions(convertedHtml);
  return convertedHtml;
}

export function isFacebookAppUAString(): boolean {
  // returns true iff the app is loaded in FB's in-app-browser (FB App or Msgr app)
  const ua = navigator.userAgent || navigator.vendor;
  return ua.indexOf('FBAN') > -1 || ua.indexOf('FBAV') > -1;
}

export async function firebaseRedirectLoginEnabled(
  deviceService: DeviceDetectorService,
): Promise<boolean> {
  // Use this function to decide if Firebase login redirect will break under Incognito mode for the current browser
  // At the time of writing, this breaks in Chrome + incognito + desktop (windows, linux, mac) where quota < 5 GB,
  // so we'll treat that as redirect login is disabled
  // Returns a promise which reolves to `true` if redirect login should work on this browser, `false` otherwise

  const isDesktopDevice = deviceService.isDesktop();
  const deviceBrowser = deviceService.getDeviceInfo().browser;
  if (isDesktopDevice && deviceBrowser.toLowerCase() === 'chrome') {
    // this is chrome on desktop
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const { quota } = await navigator.storage.estimate();
      if (quota && quota < 5000000000) {
        // quota returned less than 5 GB (incognito mode), redirect login will not work;
        return false;
      } else {
        // quota returned > 5 GB, redirect login should work.
        if (navigator.cookieEnabled) {
          // check if 3rd party cookies are also enabled. If not, facebook redirect login runs into strange issues.
          return true;
        }
        return false;
      }
    }
  }
  // check was inconclusive, redirect login *may* work;
  return true;
}

export const getTruncatedName = (name: string, length = 20): string =>
  name && name.length > length ? `${name.substring(0, length)}...` : name;

/**
 * get unique elements of array of object T
 * that contains only the property prop
 * @param obj
 * @param prop
 */
export function getUniqueBy<T>(obj: T[], prop): T[] {
  return uniqBy(obj, prop).filter((e) => e[prop]);
}

export const redirectToAppScript = (
  authMethod: string,
  username: string | null,
  password: string | null,
  fbUser: any,
) => {
  const authObject = {
    authMethod,
    username,
    password,
    refreshToken: fbUser?.refreshToken,
  };
  const encryption = CryptoJS.AES.encrypt(
    JSON.stringify(authObject),
    environment.thirdPartyEncryptionSalt,
  ).toString();
  const encryptionArr = CryptoJS.enc.Base64.parse(encryption);
  const encryptedHex = encryptionArr.toString(CryptoJS.enc.Hex);
  const url = new URL(`${environment.appScriptURL}?page=auth&param=${encryptedHex}`);
  window.location.replace(url.toString());
};

/**
 * Sanitizes a given input string by normalizing and cleaning up separators.
 *
 * This function performs the following operations on the input:
 * 1. Removes all double quotes (`"`).
 * 2. Replaces sequences of semicolons (`;`), carriage returns (`\r`),
 *    and newline characters (`\n`) with a single comma (`,`).
 * 3. Splits the modified string by commas to form an array of substrings.
 * 4. Trims whitespace from each substring.
 * 5. Removes any empty strings from the array.
 * 6. Joins the resulting array back into a single string using commas.
 *
 * @param input - The string to be sanitized.
 * @returns A sanitized string with consistent comma-separated values.
 */
function sanitizeInput(input: string): string {
  return input
    .replace(/"/g, '')
    .replace(/[;\r\n]+/g, ',') // Replace sequences of semicolons, carriage returns, and newlines with a single comma
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item.length > 0) // Remove empty entries
    .join(',');
}

/**
 * Parse comma-separated values from query parameter
 * @param param - Query parameter value (string with comma-separated values)
 * @returns Array of trimmed string values
 */
export function parseCommaSeparatedValues(param: string): string[] {
  return param.split(',').map((value) => value.trim());
}

/**
 * Extracts valid and invalid emails from the input string.
 *
 * @param input - The input string containing email addresses.
 * @param telemetry - Service for logging telemetry data.
 *
 * @param strict - If true, performs strict parsing and returns null if any invalid emails are found.
 * @returns
 * - In strict parsing mode (strict = true):
 *   - An object with `emails` array containing all valid emails and `invalidEmails` as null if all emails are valid.
 *   - `null` if there is any invalid email.
 * - In graceful parsing mode (strict = false):
 *   - An object with `emails` array containing all valid emails and `invalidEmails` as an empty array if all emails are valid.
 *   - An object with `emails` array containing valid emails and `invalidEmails` array containing invalid emails if there is at least one invalid email.
 */
export const extractEmails = (
  input: string,
  telemetry: TelemetryService,
  strict: boolean,
): { emails: string[]; invalidEmails: string[] | null } | null => {
  const sanitizedInput = sanitizeInput(input);

  if (!sanitizedInput) {
    return { emails: [], invalidEmails: [] };
  }

  if (strict) {
    return handleStrictParsing(input, telemetry);
  } else {
    return handleGracefulParsing(input, telemetry);
  }
};

/**
 * Handles strict parsing of the input string.
 * In strict mode, if any invalid emails are found, the function returns null.
 *
 * @param input - The original input string.
 * @param telemetry - Service for logging telemetry data.
 * @returns An object with valid emails and null for invalidEmails, or null if parsing fails.
 */
const handleStrictParsing = (
  input: string,
  telemetry: TelemetryService,
): { emails: string[]; invalidEmails: null } | null => {
  const sanitizedInput = sanitizeInput(input);
  const parsedList = emailAddresses.parseAddressList({
    input: sanitizedInput,
    rejectTLD: true,
    atInDisplayName: true,
    commaInDisplayName: true,
  }) as ParsedMailbox[] | null;

  if (!parsedList || parsedList.some((email) => !isValidFirebaseEmail(email.address))) {
    logInvalidEmails(telemetry, input, sanitizedInput, []);
    return null;
  }

  const emails = parsedList.map((item) => item.address);

  return {
    emails,
    invalidEmails: null,
  };
};

/**
 * Parses an input string containing potential email addresses, separating them into valid and invalid email arrays.
 * The function performs a simple split by commas, trims each resulting segment, and validates each
 * segment as a potential email address.
 *
 * If any invalid email segments are found, they are logged via the provided
 * telemetry service. The objective of this function is to attempt parsing
 * gracefully while providing feedback on any parsing issues encountered.
 *
 * @param input - The original input string containing potential email addresses,
 *                typically provided by the user or another system interface.
 *
 * @param telemetry - An instance of a telemetry service used for logging
 *                    validation information about the email parsing process.
 *                    This enables the tracking of invalid email parsing attempts
 *                    along with their context.
 *
 * @returns An object containing:
 *   - `emails`: An array of successfully parsed and validated email addresses.
 *   - `invalidEmails`: An array of segments from the input that failed email
 *                      address validation. It will be an empty array if no
 *                      invalid entries were found.
 */
const handleGracefulParsing = (
  input: string,
  telemetry: TelemetryService,
): { emails: string[]; invalidEmails: string[] } => {
  const sanitizedInput = sanitizeInput(input);
  const emailListInput = sanitizedInput.split(',');

  const emails: string[] = [];
  const invalidEmails: string[] = [];

  emailListInput.forEach((emailInput) => {
    const emailAddress = parseAndValidateEmail(emailInput);
    if (emailAddress) {
      emails.push(emailAddress);
    } else {
      invalidEmails.push(emailInput);
    }
  });

  if (invalidEmails.length > 0) {
    logInvalidEmails(telemetry, input, invalidEmails.join(','), emails);
  }

  return {
    emails,
    invalidEmails,
  };
};

function logInvalidEmails(
  telemetry: TelemetryService,
  input: string,
  invalidEmails: string,
  validEmails: string[],
) {
  telemetry.event('[Error] Invalid format for parsing emails', {
    input,
    validExtractedEmails: validEmails,
    invalidExtractedEmails: [invalidEmails],
  });
}

export const notEmpty = <TValue>(value: TValue | null | undefined): value is TValue =>
  value !== null && value !== undefined;

export const getUpdatedPermissions = (permissions: Permissions, selectedPermission: string) => {
  if (Object.values(permissions[selectedPermission]).every((val) => !!val)) {
    permissions[selectedPermission] = true;
  } else if (Object.values(permissions[selectedPermission]).every((val) => !val)) {
    permissions[selectedPermission] = false;
  }
  return permissions;
};

export const isPermissionEnabled = (
  permissions: Permissions,
  permissionName: string,
  userId: string,
): boolean => {
  if (!permissions) {
    return false;
  }
  if (typeof permissions[permissionName] === 'boolean' || !permissions[permissionName]) {
    return !!permissions[permissionName];
  } else {
    return permissions[permissionName][userId];
  }
};

export const parseTextLikeCsv = (text: string) => {
  const items = text.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);

  if (!items) {
    return null;
  }
  const cleanedItems = items.map((item) => item.replace(/"/g, '').trim());

  return cleanedItems;
};
